import React from 'react';
import { Check } from 'lucide-react';
import { WELCOME_TEMPLATES, fillWelcomeTemplate } from '../../lib/welcomeTemplates';
import { usePricing, annualPrice } from '../../lib/pricing';
import { PRESET_COLOURS, SubStatus } from '../../lib/onboardShop/helpers';

export const StepShop = ({ shop, setShop, subStatus, checkSubdomain, showWelcome = true }) => {
  const { pricing } = usePricing();
  return (
    <>
      <h3 className="admin-info-header">Shop details</h3>
      <div className="wiz-row">
        <div className="field">
          <label>Shop name</label>
          <input className="inp" value={shop.name} onChange={(e) => setShop({ ...shop, name: e.target.value })} data-testid="wiz-shop-name" />
        </div>
        <div className="field">
          <label>Subdomain <small style={{ color: 'var(--mute)' }}>.instacut.co.uk</small></label>
          <input
            className="inp"
            value={shop.subdomain}
            onChange={(e) => {
              const v = e.target.value.toLowerCase();
              setShop({ ...shop, subdomain: v });
              checkSubdomain(v);
            }}
            placeholder="sharpandsons"
            data-testid="wiz-shop-subdomain"
          />
          <SubStatus status={subStatus} />
        </div>
      </div>
      <div className="wiz-row">
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Plan</label>
          <div className="segbar" role="tablist" aria-label="Billing cycle" data-testid="wiz-shop-interval" style={{ marginBottom: 12 }}>
            <button type="button" role="tab" aria-selected={shop.interval === 'monthly'} className={`seg ${shop.interval === 'monthly' ? 'on' : ''}`} onClick={() => setShop({ ...shop, interval: 'monthly' })} data-testid="wiz-shop-interval-monthly">Monthly</button>
            <button type="button" role="tab" aria-selected={shop.interval === 'annual'} className={`seg ${shop.interval === 'annual' ? 'on' : ''}`} onClick={() => setShop({ ...shop, interval: 'annual' })} data-testid="wiz-shop-interval-annual">
              Yearly
              {Number(pricing?.annualDiscountPct || 0) > 0 && (
                <span className="seg-badge">−{Math.round(pricing.annualDiscountPct)}%</span>
              )}
            </button>
          </div>
          <div className="planpick" data-testid="wiz-shop-plan">
            {['standard', 'premium'].map((pk) => {
              const ann = annualPrice(pricing, pk);
              const monthly = pricing[pk].price;
              const isSel = shop.plan === pk;
              const label = pk === 'standard' ? 'Standard' : 'Premium';
              const tagline = pk === 'standard'
                ? 'For single-chair shops getting started'
                : 'For multi-chair shops that want everything';
              const feats = pricing[pk].features || [];
              const topFeats = feats.slice(0, 3);
              const moreCount = Math.max(0, feats.length - topFeats.length);
              const isPremium = pk === 'premium';
              return (
                <button
                  key={pk}
                  type="button"
                  onClick={() => setShop({ ...shop, plan: pk })}
                  className={`planpick-card ${isSel ? 'on' : ''} ${isPremium ? 'accent' : ''}`}
                  data-testid={`wiz-shop-plan-${pk}`}
                  aria-pressed={isSel}
                >
                  {isPremium && <span className="planpick-chip">MOST POPULAR</span>}
                  <span className={`planpick-radio ${isSel ? 'on' : ''}`} aria-hidden="true">
                    {isSel && <Check size={12} strokeWidth={3} />}
                  </span>
                  <div className="planpick-nm">{label}</div>
                  <div className="planpick-tag">{tagline}</div>
                  <div className="planpick-priceblock">
                    {shop.interval === 'monthly' ? (
                      <>
                        <div className="planpick-pr">£{monthly}<small>/mo</small></div>
                        <div className="planpick-sub">&nbsp;</div>
                      </>
                    ) : (
                      <>
                        <div className="planpick-pr">£{ann.yearly}<small>/yr</small></div>
                        <div className="planpick-sub">
                          £{ann.perMonth}/mo effective
                          {ann.saveMonthly > 0 && <> · <span className="planpick-save">save £{ann.saveMonthly}/yr</span></>}
                        </div>
                      </>
                    )}
                  </div>
                  {topFeats.length > 0 && (
                    <ul className="planpick-feats">
                      {topFeats.map((f, i) => (
                        <li key={i}><Check size={12} strokeWidth={3} />{f}</li>
                      ))}
                      {moreCount > 0 && <li className="more">+ {moreCount} more</li>}
                    </ul>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="wiz-row">
        <div className="field">
          <label>Accent colour</label>
          <div className="swatches" data-testid="wiz-shop-accent">
            {PRESET_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                className={`sw ${shop.accent_colour === c ? 'on' : ''}`}
                style={{ background: c }}
                onClick={() => setShop({ ...shop, accent_colour: c })}
                aria-label={c}
                data-testid={`wiz-shop-accent-${c.replace('#', '')}`}
              />
            ))}
          </div>
        </div>
      </div>
      {showWelcome && (
        <div className="field">
          <label>Welcome text (shows on the booking page)</label>
          <select
            className="inp"
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              setShop({ ...shop, welcome_text: fillWelcomeTemplate(e.target.value, shop.name) });
            }}
            style={{ marginBottom: 8 }}
            data-testid="wiz-shop-welcome-template"
            aria-label="Pick a default welcome message"
          >
            <option value="">Use a default message…</option>
            {WELCOME_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <textarea
            className="inp"
            rows={3}
            value={shop.welcome_text}
            onChange={(e) => setShop({ ...shop, welcome_text: e.target.value })}
            placeholder="Skin fades, hot towels, classic cuts."
            data-testid="wiz-shop-welcome"
          />
        </div>
      )}
    </>
  );
};
